import React from 'react';
import { FaLightbulb, FaRocket, FaChartLine, FaStar } from 'react-icons/fa';
import ArchetypeBadge from './ArchetypeBadge';

const ArchetypeDashboard = ({ archetype, onOpenQuiz }) => {
    const getRecommendations = (nivel) => {
        const recommendations = {
            'TRADICIONALES RESISTENTES': [
                { title: 'Configurar WhatsApp Business', icon: '📱', priority: 'Alta', description: 'Comienza con herramientas básicas de comunicación' },
                { title: 'Crear primera campaña de fidelización', icon: '🎯', priority: 'Alta', description: 'Implementa un sistema simple de puntos' },
                { title: 'Configurar redes sociales básicas', icon: '📢', priority: 'Media', description: 'Presencia digital básica' }
            ],
            'EN TRANSICIÓN': [
                { title: 'Configurar email marketing', icon: '📧', priority: 'Alta', description: 'Automatiza la comunicación con clientes' },
                { title: 'Implementar automatizaciones', icon: '⚙️', priority: 'Alta', description: 'Optimiza procesos de marketing' },
                { title: 'Analizar métricas básicas', icon: '📊', priority: 'Media', description: 'Mide el rendimiento de tus campañas' }
            ],
            'FUNCIONALES CON POTENCIAL': [
                { title: 'Optimizar campañas existentes', icon: '🎯', priority: 'Alta', description: 'Mejora el ROI de tus campañas' },
                { title: 'Implementar análisis avanzado', icon: '📈', priority: 'Alta', description: 'Análisis profundo de datos' },
                { title: 'Integrar múltiples canales', icon: '🔗', priority: 'Media', description: 'Omnicanalidad completa' }
            ],
            'EARLY ADOPTERS Y ESCALADORES': [
                { title: 'Implementar IA en marketing', icon: '🤖', priority: 'Alta', description: 'Automatización inteligente' },
                { title: 'Configurar integraciones avanzadas', icon: '🔧', priority: 'Alta', description: 'Ecosistema tecnológico completo' },
                { title: 'Optimizar para conversión', icon: '💰', priority: 'Media', description: 'Maximiza el valor del cliente' }
            ]
        };

        return recommendations[nivel] || [];
    };

    const getArchetypeStats = (archetype) => {
        const stats = {
            'TRADICIONALES RESISTENTES': {
                digitalMaturity: 25,
                marketingReadiness: 30,
                automationLevel: 20,
                growthPotential: 40
            },
            'EN TRANSICIÓN': {
                digitalMaturity: 50,
                marketingReadiness: 60,
                automationLevel: 45,
                growthPotential: 70
            },
            'FUNCIONALES CON POTENCIAL': {
                digitalMaturity: 75,
                marketingReadiness: 80,
                automationLevel: 70,
                growthPotential: 85
            },
            'EARLY ADOPTERS Y ESCALADORES': {
                digitalMaturity: 90,
                marketingReadiness: 95,
                automationLevel: 90,
                growthPotential: 95
            }
        };

        return stats[archetype.nivel] || {};
    };

    if (!archetype) {
        return (
            <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="text-center">
                    <div className="text-4xl mb-4">🎯</div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        Identifica tu Perfil de Negocio
                    </h3>
                    <p className="text-gray-600 mb-4">
                        Completa el cuestionario para obtener recomendaciones personalizadas
                    </p>
                    <button
                        onClick={onOpenQuiz}
                        className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                    >
                        Comenzar Cuestionario
                    </button>
                </div>
            </div>
        );
    }

    const recommendations = getRecommendations(archetype.nivel);
    const stats = getArchetypeStats(archetype);

    return (
        <div className="space-y-6">
            {/* Archetype Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <ArchetypeBadge archetype={archetype} className="mr-4" />
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">
                                Tu Perfil: {archetype.nombrePerfil}
                            </h3>
                            <p className="text-sm text-gray-600">
                                {archetype.descripcionPerfil}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onOpenQuiz}
                        className="text-blue-500 hover:text-blue-700 text-sm font-medium"
                    >
                        Actualizar perfil
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Madurez Digital</span>
                        <span className="text-lg font-semibold text-blue-600">{stats.digitalMaturity}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${stats.digitalMaturity}%` }}
                        ></div>
                    </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Marketing</span>
                        <span className="text-lg font-semibold text-green-600">{stats.marketingReadiness}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${stats.marketingReadiness}%` }}
                        ></div>
                    </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Automatización</span>
                        <span className="text-lg font-semibold text-purple-600">{stats.automationLevel}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${stats.automationLevel}%` }}
                        ></div>
                    </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Potencial</span>
                        <span className="text-lg font-semibold text-orange-600">{stats.growthPotential}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${stats.growthPotential}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            {/* Recommendations */}
            <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-center mb-4">
                    <FaLightbulb className="text-yellow-500 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-800">
                        Acciones Recomendadas
                    </h3>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                    {recommendations.map((rec, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-2xl">{rec.icon}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${rec.priority === 'Alta'
                                        ? 'bg-red-100 text-red-700'
                                        : 'bg-blue-100 text-blue-700'
                                    }`}>
                                    {rec.priority}
                                </span>
                            </div>
                            <h4 className="font-medium text-gray-800 mb-1">{rec.title}</h4>
                            <p className="text-sm text-gray-600">{rec.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-center mb-4">
                    <FaRocket className="text-green-500 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-800">
                        Acciones Rápidas
                    </h3>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <button className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
                        <FaChartLine className="text-blue-500 mr-3 text-lg" />
                        <div>
                            <h4 className="font-medium text-gray-800">Ver Métricas</h4>
                            <p className="text-sm text-gray-600">Analiza tu rendimiento</p>
                        </div>
                    </button>

                    <button className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
                        <FaStar className="text-yellow-500 mr-3 text-lg" />
                        <div>
                            <h4 className="font-medium text-gray-800">Crear Campaña</h4>
                            <p className="text-sm text-gray-600">Lanza una nueva campaña</p>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ArchetypeDashboard; 